import {
  Alert,
  Badge,
  Button,
  Card,
  type CSSVariablesResolver,
  Modal,
  Drawer,
  Menu,
  Paper,
  Select,
  Table,
  Tabs,
  TextInput,
  Textarea,
  NumberInput,
  PasswordInput,
  Title,
  Tooltip,
  createTheme,
} from '@mantine/core';
import { RADIUS, SEMANTIC_VARS, SPACING, gold, sand, teal } from './tokens.js';

/**
 * The platform theme every screen inherits from (§6).
 *
 * Typographic semantics, enforced here rather than left to each screen:
 *
 *   Cormorant  — H1 and H2 only. What a page *is*.
 *   Montserrat — everything else: navigation, buttons, labels, badges, forms,
 *                tables, data and system messages.
 *
 * A product's visual theme never reaches this file. Board art and table
 * textures live inside product surfaces (see `product.ts`).
 */
export const theme = createTheme({
  primaryColor: 'teal',
  primaryShade: 6,

  colors: { teal, gold, sand },

  white: '#ffffff',
  black: 'var(--text-primary)',

  fontFamily: 'var(--font-ui)',
  fontFamilyMonospace: 'var(--font-mono)',

  headings: {
    fontFamily: 'var(--font-display)',
    // Cormorant is variable (300–700), so 600 is a real weight, not a synthesis.
    fontWeight: '600',
    sizes: {
      h1: { fontSize: 'clamp(2rem, 1.4rem + 1.9vw, 2.9rem)', lineHeight: '1.14' },
      h2: { fontSize: 'clamp(1.55rem, 1.2rem + 1.1vw, 2.1rem)', lineHeight: '1.2' },
      // h3–h6 are structural, so they belong to the interface font (see global.css).
      h3: { fontSize: '1.2rem', lineHeight: '1.35', fontWeight: '600' },
      h4: { fontSize: '1.0625rem', lineHeight: '1.4', fontWeight: '600' },
      h5: { fontSize: '0.9375rem', lineHeight: '1.45', fontWeight: '600' },
      h6: { fontSize: '0.8125rem', lineHeight: '1.5', fontWeight: '600' },
    },
  },

  fontSizes: {
    xs: '0.75rem',
    sm: '0.8125rem',
    md: '0.9375rem',
    lg: '1.0625rem',
    xl: '1.1875rem',
  },

  lineHeights: { xs: '1.45', sm: '1.5', md: '1.6', lg: '1.6', xl: '1.6' },

  spacing: {
    xs: `${SPACING.xs}px`,
    sm: `${SPACING.sm}px`,
    md: `${SPACING.md}px`,
    lg: `${SPACING.lg}px`,
    xl: `${SPACING.xl}px`,
  },

  radius: {
    xs: `${RADIUS.xs}px`,
    sm: `${RADIUS.sm}px`,
    md: `${RADIUS.md}px`,
    lg: `${RADIUS.lg}px`,
    xl: `${RADIUS.xl}px`,
  },

  defaultRadius: 'md',

  shadows: {
    xs: 'none',
    sm: 'var(--shadow-card)',
    md: 'var(--shadow-card-hover)',
    lg: 'var(--shadow-elevated)',
    xl: 'var(--shadow-elevated)',
  },

  focusRing: 'auto',
  cursorType: 'pointer',

  components: {
    /** Controls never wear the display face — that would be ornament. */
    Button: Button.extend({
      defaultProps: { radius: 'md' },
      styles: { root: { fontFamily: 'var(--font-ui)', fontWeight: 600, letterSpacing: '0.005em' } },
      vars: (_theme, props) => ({
        root: {
          '--button-height':
            props.size === 'lg'
              ? 'var(--control-height-lg)'
              : props.size === 'xs' || props.size === 'sm'
                ? 'var(--control-height-sm)'
                : 'var(--control-height-md)',
        },
      }),
    }),

    Title: Title.extend({
      styles: { root: { letterSpacing: '-0.005em' } },
    }),

    /** A card is a faint outline on ivory, not a floating panel. */
    Paper: Paper.extend({
      defaultProps: { radius: 'lg', bg: 'var(--surface-card)', shadow: undefined },
    }),

    Card: Card.extend({
      defaultProps: { radius: 'lg', padding: 'md', bg: 'var(--surface-card)', shadow: undefined },
    }),

    Modal: Modal.extend({
      defaultProps: {
        radius: 'lg',
        centered: true,
        // No blur: frosted panels are not part of this system.
        overlayProps: { backgroundOpacity: 0.4 },
        transitionProps: { transition: 'pop', duration: 150 },
      },
    }),

    Drawer: Drawer.extend({
      defaultProps: { radius: 0, position: 'right', size: 'md', overlayProps: { backgroundOpacity: 0.4 } },
    }),

    Menu: Menu.extend({
      defaultProps: { radius: 'md', shadow: 'lg', withinPortal: true },
    }),

    Tabs: Tabs.extend({
      defaultProps: { variant: 'default', radius: 'md' },
    }),

    Badge: Badge.extend({
      defaultProps: { radius: 'sm', variant: 'light', tt: 'none', fw: 600 },
      styles: { label: { fontFamily: 'var(--font-ui)', letterSpacing: '0.01em' } },
    }),

    Alert: Alert.extend({
      defaultProps: { radius: 'md', variant: 'light' },
      styles: { title: { fontFamily: 'var(--font-ui)', fontWeight: 600 } },
    }),

    Table: Table.extend({
      styles: { th: { fontFamily: 'var(--font-ui)', fontWeight: 600 } },
    }),

    Tooltip: Tooltip.extend({
      defaultProps: { radius: 'sm', withArrow: true, openDelay: 250 },
    }),

    TextInput: TextInput.extend({ defaultProps: { radius: 'md' } }),
    PasswordInput: PasswordInput.extend({ defaultProps: { radius: 'md' } }),
    Textarea: Textarea.extend({ defaultProps: { radius: 'md' } }),
    NumberInput: NumberInput.extend({ defaultProps: { radius: 'md' } }),
    Select: Select.extend({ defaultProps: { radius: 'md', checkIconPosition: 'right' } }),
  },

  other: {
    /** Max width of a reading/content column — keeps long text editorial. */
    contentMaxWidth: 1180,
    navWidth: 248,
  },
});

/**
 * Publishes the platform variables into the document.
 *
 * The prototype ships light-only; the dark slot is kept so a future dark theme
 * is a token change rather than a component rewrite.
 */
export const cssVariablesResolver: CSSVariablesResolver = () => ({
  variables: SEMANTIC_VARS,
  light: {},
  dark: {},
});
